import { useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Flex } from '@zillow/constellation';
import { Box, Text, TextButton } from '@zillow/constellation-10';
import { cloneDeep } from 'es-toolkit';

import { useSelector } from '../../../../selectors/useSelector';

import { MODAL_COMPONENT } from '../constants';
import { NOT_APPLICABLE, NOT_SET, TYPES_MAP } from '../../../../constants/ListingCriteriaConstants';
import { RECS, REVIEW_PAGE_SECTION_RECS } from '../../../../constants/RecommendationsConstants';

import listingCriteriaUtils from '../../../../utils/listingCriteriaUtils';
import numberUtils from '../../../../utils/numberUtils';

import ContentSectionEditButton from './ContentSectionEditButton';
import FieldValueDisplay from './FieldValueDisplay';
import RecommendationsLearnMoreContent from '../../../RecommendationsLearnMoreContent';
import RecommendationIconBulb from '../../../RecommendationIconBulb';

import stringHelper from '../../../../utils/stringHelper';

import { StyledIconPlaceholder, StyledReviewSection, StyledReviewTitleRow } from '../StyledComponents';
import { ReviewSectionTitle } from '../../../../styles/wizard';

import AccordionIconError from './AccordionIconError';

import type { MouseEvent } from 'react';
import type { CriterionProps } from '../../../../model/ListingCriteria/Criterion';

const ariaLabels = {
  FINANCIAL_EXPECTATIONS: 'financial-expectations',
  PETS: 'pets',
} as const;

const criteriaPetCountToValidateList = [
  TYPES_MAP.PET_POLICY_CAT_COUNT,
  TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT,
  TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT,
];

// smallDogCount, largeDogCount, and catCount criteria is empty string
const getPetsAllowed = (criteriaMap: Array<CriterionProps>) => {
  const petCriteria = cloneDeep(criteriaMap).filter((criterion) => {
    return criterion.type.includes('Count') && criterion.type !== TYPES_MAP.PET_POLICY_DOG_COUNT;
  });

  const hasNonEmptyCriterion = petCriteria.some((criterion) => {
    return criterion.criterion !== '';
  });

  // indicates no selections have been made
  if (!hasNonEmptyCriterion) return null;

  /**
   * a selection has been made
   * updates empty string values to be 0 for pet count criteria
   */
  petCriteria.forEach((criterion) => {
    if (
      criteriaPetCountToValidateList.includes(criterion.type as (typeof criteriaPetCountToValidateList)[number]) &&
      criterion.criterion === ''
    ) {
      criterion.criterion = '0';
    }
  });

  const hasNonZeroCriterion = petCriteria.some(
    (criterion) =>
      criteriaPetCountToValidateList.includes(criterion.type as (typeof criteriaPetCountToValidateList)[number]) &&
      criterion.criterion !== '0',
  );

  return hasNonZeroCriterion;
};

const getPetsAllowedViaDTOUnset = (petsAllowed: {
  smallDogsDto: { allowed: boolean; count: number };
  largeDogsDto: { allowed: boolean; count: number };
  catsDto: { allowed: boolean; count: number };
}) => {
  const isSmallDogsDtoNull = petsAllowed.smallDogsDto.allowed === null;
  const isLargeDogsDtoNull = petsAllowed.largeDogsDto.allowed === null;
  const isCatsDtoNull = petsAllowed.catsDto.allowed === null;

  const arePetsAllowedViaDTO = isSmallDogsDtoNull && isLargeDogsDtoNull && isCatsDtoNull;

  return arePetsAllowedViaDTO;
};

const getPetsAllowedViaDTO = (petsAllowed: {
  smallDogsDto: { allowed: boolean; count: number };
  largeDogsDto: { allowed: boolean; count: number };
  catsDto: { allowed: boolean; count: number };
}) => {
  // loop through petsAllowed and check
  const arePetsAllowedViaDTO =
    petsAllowed.smallDogsDto.allowed || petsAllowed.largeDogsDto.allowed || petsAllowed.catsDto.allowed;

  return arePetsAllowedViaDTO;
};

type Props = {
  onEditClick: (e: MouseEvent<HTMLButtonElement>) => void;
};

const ScreeningCriteriaContent = ({ onEditClick }: Props) => {
  const { criteriaList, petsAllowed, recommendations } = useSelector((state) => {
    return {
      criteriaList: state?.properties?.currentProperty?.listingCriteria?.criteria,
      petsAllowed: state?.properties?.currentProperty?.petsAllowed,
      recommendations: state?.properties?.recommendations,
    };
  }, shallowEqual);

  const [displayRecsModal, setDisplayRecsModal] = useState('');

  /**
   * TODO: add this logic
   *
   * cross reference with petsAllowed?
   *
   * smallDogsDto is empy -> small dogs not set
   * smallDogsDto.allowed = false -> small dogs not allowed
   * smallDogsDto.allowed = true, count is empty -> small dogs allowed
   * smallDogsDto.allowed = true, count = 2 -> 2 small dogs allowed
   *
   * after any criteria updates we need to refetch get details endpoint
   * also refetch /api/v1/users/properties/fees/singleFamily/get
   */

  // fallback to null
  // @ts-expect-error
  const areAllPetsNotAllowedDTO = Object.values(petsAllowed).some((pet) => pet.allowed === null)
    ? null
    : // @ts-expect-error
      Object.values(petsAllowed).some((pet) => pet.allowed === false);

  const {
    creditScore,
    isCreditScoreEnabled,
    isCreditScoreNegotiable,
    isRentToIncomeRatioEnabled,
    isRentToIncomeRatioNegotiable,
    petCriteria,
    rentToIncomeRatio,
  } = useMemo(() => {
    const getCriterion = (type: string) => listingCriteriaUtils.getCriteriaByType(criteriaList, type);

    const petCriteria = {
      catCount: getCriterion(TYPES_MAP.PET_POLICY_CAT_COUNT).criterion,
      isCatCountNegotiable: getCriterion(TYPES_MAP.PET_POLICY_CAT_COUNT).negotiable,
      smallDogCount: getCriterion(TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT).criterion,
      isSmallDogCountNegotiable: getCriterion(TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT).negotiable,
      largeDogCount: getCriterion(TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT).criterion,
      isLargeDogCountNegotiable: getCriterion(TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT).negotiable,
      petDeposit: getCriterion(TYPES_MAP.PET_POLICY_PET_DEPOSIT).criterion,
      petRent: getCriterion(TYPES_MAP.PET_POLICY_PET_RENT).criterion,
      dogSize: getCriterion(TYPES_MAP.PET_POLICY_DOG_SIZE).criterion,
      isPetDepositRefundable: getCriterion(TYPES_MAP.PET_POLICY_PET_DEPOSIT).refundable ?? null,
    };

    return {
      creditScore: getCriterion(TYPES_MAP.CREDIT_SCORE).criterion,
      isCreditScoreEnabled: getCriterion(TYPES_MAP.CREDIT_SCORE).isEnabled ?? true,
      isCreditScoreNegotiable: getCriterion(TYPES_MAP.CREDIT_SCORE).negotiable ?? false,
      isRentToIncomeRatioEnabled: getCriterion(TYPES_MAP.INCOME_TO_RENT).isEnabled ?? true,
      isRentToIncomeRatioNegotiable: getCriterion(TYPES_MAP.INCOME_TO_RENT).negotiable ?? false,
      petCriteria,
      rentToIncomeRatio: getCriterion(TYPES_MAP.INCOME_TO_RENT).criterion,
    };
  }, [criteriaList]);

  const arePetsAllowed = getPetsAllowed(criteriaList);
  const arePetsAllowedViaDTO = getPetsAllowedViaDTO(petsAllowed);

  const isPetPolicyNegotiable =
    petCriteria.isSmallDogCountNegotiable || petCriteria.isLargeDogCountNegotiable || petCriteria.isCatCountNegotiable;

  const petDisplays = useMemo(() => {
    let displayCatsWithNoCount = false;
    let displayLargeDogWithNoCount = false;
    let displaySmallOrMediumDogWithNoCount = false;

    const hasPetCriteriaCatsCountBeenSet = petCriteria.catCount !== '';
    const hasPetCriteriaSmallDogCountBeenSet = petCriteria.smallDogCount !== '';
    const hasPetCriteriaLargeDogCountBeenSet = petCriteria.largeDogCount !== '';

    const isPetCriteriaCatCountZero = petCriteria.catCount === '0';
    const isPetCriteriaSmallDogCountZero = petCriteria.smallDogCount === '0';
    const isPetCriteriaLargeDogCountZero = petCriteria.largeDogCount === '0';

    // need to cross reference legacy data
    const areAllPetsAllowedFalse =
      petsAllowed?.catsDto?.allowed === false &&
      petsAllowed?.smallDogsDto?.allowed === false &&
      petsAllowed?.largeDogsDto?.allowed === false;

    const areAllPetCriteriaZero =
      isPetCriteriaCatCountZero && isPetCriteriaSmallDogCountZero && isPetCriteriaLargeDogCountZero;

    // check each pet criteria
    if (petsAllowed?.catsDto?.allowed && hasPetCriteriaCatsCountBeenSet === false) {
      displayCatsWithNoCount = true;
    }

    if (petsAllowed?.smallDogsDto?.allowed && hasPetCriteriaSmallDogCountBeenSet === false) {
      displaySmallOrMediumDogWithNoCount = true;
    }

    if (petsAllowed?.largeDogsDto?.allowed && hasPetCriteriaLargeDogCountBeenSet === false) {
      displayLargeDogWithNoCount = true;
    }

    // set pet displays
    const catDisplay = hasPetCriteriaCatsCountBeenSet
      ? isPetCriteriaCatCountZero
        ? 'No cats'
        : stringHelper.pluralize(petCriteria.catCount, 'cat', 'cats')
      : // fallback
        displayCatsWithNoCount && 'Cats allowed';

    const smallDogDisplay = hasPetCriteriaSmallDogCountBeenSet
      ? isPetCriteriaSmallDogCountZero
        ? 'No small dogs'
        : stringHelper.pluralize(
            petCriteria.smallDogCount,
            'small dog under 40 pounds',
            'small dogs under 40 pounds each',
          )
      : // fallback
        displaySmallOrMediumDogWithNoCount && 'Small dog under 40 pounds';

    const largeDogDisplay = hasPetCriteriaLargeDogCountBeenSet
      ? isPetCriteriaLargeDogCountZero
        ? 'No large dogs'
        : stringHelper.pluralize(
            petCriteria.largeDogCount,
            'large dog over 40 pounds',
            'large dogs over 40 pounds each',
          )
      : // fallback
        displayLargeDogWithNoCount && 'Large dog over 40 pounds';

    // non pet criteria
    const petDepositDisplay =
      petCriteria.petDeposit === ''
        ? ''
        : `Pet deposit: ${numberUtils.asCurrency(Number(petCriteria.petDeposit))} ${petCriteria.isPetDepositRefundable === false ? '' : '(refundable)'}`;

    const petRentDisplay =
      petCriteria.petRent === ''
        ? ''
        : `Pet rent: ${numberUtils.asCurrency(Number(petCriteria.petRent))} pet rent per month`;

    const petPolicyDisplay =
      isPetPolicyNegotiable === null ? '' : `Pet policy is ${isPetPolicyNegotiable ? 'negotiable' : 'non-negotiable'}`;

    return {
      catDisplay,
      areAllPetsAllowedFalse,
      areAllPetCriteriaZero,
      smallDogDisplay,
      largeDogDisplay,
      petDepositDisplay,
      petRentDisplay,
      petPolicyDisplay,
    };
  }, [petsAllowed, petCriteria, isPetPolicyNegotiable]);

  const financialDisplays = useMemo(() => {
    const isRentToIncomeRatioNegotiableDisplay =
      listingCriteriaUtils.getNegotiableDisplay(isRentToIncomeRatioNegotiable);

    const isCreditScoreNegotiableDisplay = listingCriteriaUtils.getNegotiableDisplay(isCreditScoreNegotiable);

    const getRentToIncomeRatioDisplay = () => {
      let response = rentToIncomeRatio;
      let suffix = `(${isRentToIncomeRatioNegotiableDisplay})`;

      if (rentToIncomeRatio === '' || rentToIncomeRatio === NOT_APPLICABLE) {
        response = NOT_SET;
        suffix = isRentToIncomeRatioNegotiable ? suffix : '';
      }

      return `${response} ${suffix}`;
    };

    const getCreditScoreDisplay = () => {
      let response = creditScore;
      let suffix = `(${isCreditScoreNegotiableDisplay})`;

      if (creditScore === '' || creditScore === NOT_APPLICABLE) {
        response = NOT_SET;
        suffix = isCreditScoreNegotiable ? suffix : '';
      } else {
        suffix = response === '740+' ? suffix : `or above ${suffix}`;
      }

      return `${response} ${suffix}`;
    };

    return {
      rentToIncomeRatioDisplay: `Income-to-rent ratio: ${getRentToIncomeRatioDisplay()}`,
      creditScoreDisplay: `Credit score: ${getCreditScoreDisplay()}`,
    };
  }, [isRentToIncomeRatioNegotiable, rentToIncomeRatio, isCreditScoreNegotiable, creditScore]);

  const renderFinancialExpectationsDisplay = useMemo(() => {
    return (
      <div data-testid="FinancialExpectationsDisplay">
        {isRentToIncomeRatioEnabled && (
          <Text asChild>
            <p>{financialDisplays.rentToIncomeRatioDisplay}</p>
          </Text>
        )}
        {isCreditScoreEnabled && (
          <Text asChild>
            <p>{financialDisplays.creditScoreDisplay}</p>
          </Text>
        )}
      </div>
    );
  }, [financialDisplays]);

  const renderPetsDisplay = useMemo(() => {
    const {
      catDisplay,
      areAllPetCriteriaZero,
      smallDogDisplay,
      largeDogDisplay,
      petDepositDisplay,
      petRentDisplay,
      petPolicyDisplay,
    } = petDisplays;

    return (
      <div data-testid="PetsDisplay">
        {arePetsAllowedViaDTO === null ? (
          <Text color="text.subtle" asChild>
            <p>Action needed</p>
          </Text>
        ) : (
          <>
            {areAllPetCriteriaZero ? (
              <Text asChild>
                <p data-testid="NoPetsDisplay">No pets</p>
              </Text>
            ) : (
              <Box css={{ marginBottom: '200' }} data-testid="PetsDisplayList">
                <Text asChild>
                  <p>{catDisplay}</p>
                </Text>
                <Text asChild>
                  <p>{smallDogDisplay}</p>
                </Text>
                <Text asChild>
                  <p>{largeDogDisplay}</p>
                </Text>
                <Box css={{ marginTop: '200' }}>
                  <Text asChild>
                    <p>{petDepositDisplay}</p>
                  </Text>
                </Box>
                <Text asChild>
                  <p>{petRentDisplay}</p>
                </Text>
              </Box>
            )}
          </>
        )}

        <Text asChild>
          <p data-testid="PetPolicyDisplay">{petPolicyDisplay}</p>
        </Text>
      </div>
    );
  }, [petDisplays]);

  return (
    <div data-testid="ScreeningCriteriaContent" data-group-title="ScreeningCriteria">
      <StyledReviewSection data-testid="FinancialExpectations">
        <StyledReviewTitleRow>
          <Flex display="flex" alignItems="center">
            <StyledIconPlaceholder />
            <Text textStyle="body-bold">Financial expectations</Text>
          </Flex>

          <ContentSectionEditButton
            allowFeedEdit={true}
            buttonFieldName={ariaLabels.FINANCIAL_EXPECTATIONS}
            value={MODAL_COMPONENT.FinancialExpectationsFormSectionContainer.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>

        <FieldValueDisplay value={[renderFinancialExpectationsDisplay]} />
      </StyledReviewSection>

      <StyledReviewSection data-testid="Pets">
        <StyledReviewTitleRow>
          <Flex display="flex" alignItems="center">
            <StyledIconPlaceholder>
              {arePetsAllowedViaDTO === null ? (
                <AccordionIconError tabIndex={0} title="Pets incomplete error" marginRight="xs" />
              ) : recommendations[REVIEW_PAGE_SECTION_RECS.SCREENING_CRITERIA][RECS.PETS_NOT_ALLOWED] ? (
                <RecommendationIconBulb
                  fieldName={ariaLabels.PETS}
                  removeFlex={true}
                  onClick={() => {
                    setDisplayRecsModal(RECS.PETS_NOT_ALLOWED);
                  }}
                />
              ) : null}
            </StyledIconPlaceholder>
            <ReviewSectionTitle>
              <Text textStyle="body-bold">Pets</Text>
              <TextButton
                aria-label={`learn-more-about-${ariaLabels.PETS}`}
                textStyle="body"
                onClick={() => {
                  setDisplayRecsModal(RECS.PETS_NOT_ALLOWED);
                }}
              >
                Learn more
              </TextButton>
            </ReviewSectionTitle>
          </Flex>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.PETS}
            value={MODAL_COMPONENT.PetsAllowedFormSectionContainer.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>

        <FieldValueDisplay value={[renderPetsDisplay]} />
      </StyledReviewSection>

      {displayRecsModal && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal('')}
          recommendationType={displayRecsModal}
        />
      )}
    </div>
  );
};

export default ScreeningCriteriaContent;
